import {
  notEmptyFields,
  type Optional,
  type SetRequiredAndNonNullable,
} from '@festina-life/core'
import cx from 'classnames'
import dayjs, { type Dayjs } from 'dayjs'

import type {
  DateType,
  MomentItem,
  MomentItemWithOverlaps,
  PeriodItem,
  RowItems,
  StackPosition,
} from './types'

export const TIMELINE_SIZES = {
  MONTH_SIZE: 28 as const,
  MONTH_GAP: 2 as const,
  MONTHS_BEFORE: 0 as const,
  MONTHS_AFTER: 3 as const,
  ROW_GAP: 10 as const,
  PERIOD_STACK_GAP: 2 as const,
  PERIOD_TOP_OFFSET(offset: number) {
    return offset * (this.MONTH_SIZE + this.PERIOD_STACK_GAP)
  },
  PADDING_OUTER: 10 as const,
  WIDTH(months: number) {
    return this.MONTH_SIZE * months + this.MONTH_GAP * (months - 1)
  },
  LEFT_OFFSET({
    months,
    hasPadding = true,
  }: {
    months: number
    hasPadding?: boolean
  }) {
    return (
      (this.MONTH_SIZE + this.MONTH_GAP) * months +
      (hasPadding ? this.PADDING_OUTER : 0)
    )
  },
  LEFT_OFFSET_TO_DATE({
    startDate,
    currentDate,
  }: {
    startDate: Dayjs
    currentDate: Dayjs
  }) {
    const months = monthsInPeriod({
      startDate,
      endDate: currentDate,
    })
    return this.LEFT_OFFSET({
      months: months + currentDate.date() / currentDate.daysInMonth(),
      hasPadding: true,
    })
  },
  MINIMAP_ROW_HEIGHT: 4 as const,
  MINIMAP_MONTHS_GAP: 1 as const,
  MINIMAP_PADDING: 8 as const,
  MINIMAP_ROW_GAP: 3 as const,
  MINIMAP_HEIGHT(rowsCount: number) {
    return (
      rowsCount * this.MINIMAP_ROW_HEIGHT +
      this.MINIMAP_ROW_GAP * (rowsCount - 1)
    )
  },
  MINIMAP_WIDTH(months: number) {
    return (
      this.MINIMAP_ROW_HEIGHT * months + this.MINIMAP_MONTHS_GAP * (months - 1)
    )
  },
  AXIS_MARK_HEIGHT: 12 as const,
  AXIS_HEIGHT: 30 as const,
  AXIS_WIDTH(months: number) {
    return (
      this.MONTH_SIZE * months +
      this.MONTH_GAP * (months - 1) +
      this.PADDING_OUTER * 2
    )
  },
  AXIS_BOTTOM_MARGIN: 6 as const,
}

export const monthsInPeriod = ({
  startDate,
  endDate,
  isInclusive = false,
}: {
  startDate: Dayjs
  endDate: Dayjs
  isInclusive?: boolean
}) => endDate.diff(startDate, 'month') + (isInclusive ? 1 : 0)

export const getEndDateAdjustedFromFirstOfMonth = (endDate: Optional<Dayjs>) =>
  endDate?.startOf('month').isSame(endDate)
    ? endDate.subtract(1, 'day')
    : endDate

export function toDayjs(date: string): Dayjs
export function toDayjs(date: Optional<string>): Dayjs | undefined
export function toDayjs(date: Optional<string>) {
  return date ? dayjs(date) : undefined
}

export const toMomentWithDayjs = (
  moment: MomentItemWithOverlaps,
): MomentItemWithOverlaps<Dayjs> => ({
  ...moment,
  startDate: toDayjs(moment.startDate),
  overlappingMoments: moment.overlappingMoments?.map((overlappingMoment) => ({
    ...overlappingMoment,
    startDate: toDayjs(overlappingMoment.startDate),
  })),
})

export const toRowsWithDayjs = (rows: RowItems[]): RowItems<Dayjs>[] =>
  rows.map((items) =>
    items.map((item) =>
      isPeriodItem(item)
        ? {
            ...item,
            startDate: toDayjs(item.startDate),
            endDate: toDayjs(item.endDate),
            moments: item.moments?.map(toMomentWithDayjs),
          }
        : { ...item, startDate: toDayjs(item.startDate) },
    ),
  )

export const getTimelineRange = ({
  rows,
  currentDate,
  containerWidth,
}: {
  rows: RowItems<Dayjs>[]
  currentDate: Dayjs
  containerWidth: number
}) => {
  const flatRows = rows.flat()
  const endDate = flatRows
    .reduce((max, item) => {
      const latestItemDate =
        isPeriodItem(item) && item.endDate ? item.endDate : item.startDate
      return latestItemDate?.isAfter(max) ? latestItemDate : max
    }, currentDate)
    .endOf('month')
    .add(TIMELINE_SIZES.MONTHS_AFTER, 'month')
  const dataStartDate = flatRows
    .reduce(
      (min, { startDate }) => (startDate?.isBefore(min) ? startDate : min),
      endDate,
    )
    .startOf('month')
    .add(-TIMELINE_SIZES.MONTHS_BEFORE, 'month')
  const monthsToFillContainer = Math.max(
    1,
    Math.floor(
      (containerWidth -
        (TIMELINE_SIZES.PADDING_OUTER * 2 - TIMELINE_SIZES.MONTH_GAP)) /
        (TIMELINE_SIZES.MONTH_SIZE + TIMELINE_SIZES.MONTH_GAP),
    ),
  )
  const startDate = dayjs.min(
    dataStartDate,
    endDate.subtract(monthsToFillContainer - 1, 'month').startOf('month'),
  )

  return {
    startDate,
    endDate,
    months: monthsInPeriod({ startDate, endDate, isInclusive: true }),
    currentDatePosition: TIMELINE_SIZES.LEFT_OFFSET_TO_DATE({
      startDate,
      currentDate,
    }),
    dataStartDate,
  }
}

export const amountFlowToColor = (flow: 'in' | 'out') =>
  ({
    in: 'blue' as const,
    out: 'emerald' as const,
  })[flow]

export const isPeriodItem = <D extends DateType>(
  item: RowItems<D>[number],
): item is PeriodItem<D> => 'endDate' in item

type RowItemWithDateKey<D extends DateType> = RowItems<D>[number] & {
  dateKey: string
}

type StackPositionMeta<D extends DateType> = {
  dateKey: string
  stackPosition: StackPosition
  overlappingMoments?: MomentItem<D>[]
}

const isPeriodWithStartDate = <D extends DateType>(
  item: RowItems<D>[number],
): item is SetRequiredAndNonNullable<PeriodItem<D>, 'startDate'> =>
  isPeriodItem(item) && Boolean(item.startDate)

const isBefore = (a: string | Dayjs, b: string | Dayjs) =>
  dayjs(a).isBefore(dayjs(b))

const isOverlapping = <D extends DateType>({
  a,
  b,
  parentEndDate,
}: {
  a: RowItems<D>[number]
  b: RowItems<D>[number]
  parentEndDate: Dayjs
}) =>
  isPeriodWithStartDate(a) &&
  isPeriodWithStartDate(b) &&
  isBefore(a.startDate, b.endDate ?? parentEndDate) &&
  isBefore(b.startDate, a.endDate ?? parentEndDate)

/**
 * Given a flat array of timeline periods and whether the group is currently
 * expanded, computes the vertical position (0-based) for every period that
 * overlaps with at least one other period in the same group.
 *
 * Uses a greedy interval-scheduling approach so that non-overlapping periods
 * can share a position, minimizing the total number of stacked positions needed.
 *
 * NOTE: This function assumes that `items` are provided in a suitable order,
 * typically with all period items for a row sorted by ascending `startDate`.
 * If callers pass items in an arbitrary order, the position assignment will still
 * be deterministic, but it may allocate more positions than the true maximum
 * simultaneous overlap, making expanded rows taller than strictly necessary.
 *
 *
 * Returns:
 *   overlapPositions — map from item index → position index (only populated for
 *                      periods that actually overlap something)
 *   overlapCount     — number of distinct positions used; drives the period height
 */
export const computePeriodOverlap = <D extends DateType>({
  items,
  isExpanded,
  parentEndDate,
}: {
  items: (RowItems<D>[number] & StackPositionMeta<D>)[]
  isExpanded: boolean
  parentEndDate: Dayjs
}): { overlapPositions: Map<number, number>; overlapCount: number } => {
  const overlapPositions = new Map<number, number>()

  items.forEach((item, itemIndex) => {
    // Only periods have end dates
    if (!isPeriodItem(item)) {
      return
    }

    // Check whether this period overlaps in time with any other period in the same row
    const hasOverlaps = items.some(
      (other, otherItemIndex) =>
        otherItemIndex !== itemIndex &&
        isOverlapping({ a: item, b: other, parentEndDate }),
    )

    if (!hasOverlaps) {
      return
    }

    // When collapsed we don't need real position values — all periods
    // render at topOffset 0. We just need the item recorded in the map so that
    // the user can expand and see the periods. Skip the greedy algorithm entirely.
    if (!isExpanded) {
      overlapPositions.set(itemIndex, 0)
      return
    }

    // Greedy interval scheduling: assign the lowest vertical position (0-based)
    // not already occupied by a period that overlaps with this one in time.
    // Periods that don't overlap in time can safely share a position,
    // reducing the total number of stacked rows needed.
    const takenPositions = new Set<number>()

    for (const [overlappingItemIndex, position] of overlapPositions) {
      const other = items[overlappingItemIndex]
      if (isOverlapping({ a: item, b: other, parentEndDate })) {
        takenPositions.add(position)
      }
    }

    // Walk up until we find a position no conflicting period is using
    // Position 0 is reserved for 'front' items; 'behind' items must start at 1
    let position = item.stackPosition === 'behind' ? 1 : 0
    while (takenPositions.has(position)) {
      position++
    }
    overlapPositions.set(itemIndex, position)
  })

  // +1 converts 0-based max position index to a count
  const overlapCount =
    overlapPositions.size > 0 ? Math.max(...overlapPositions.values()) + 1 : 0

  return { overlapPositions, overlapCount }
}

export function getItemsWithStackPosition<D extends DateType>(
  items: MomentItem<D>[],
): (MomentItem<D> & StackPositionMeta<D>)[]
export function getItemsWithStackPosition<D extends DateType>(
  items: RowItems<D>,
): (RowItems<D>[number] & StackPositionMeta<D>)[]
export function getItemsWithStackPosition<D extends DateType>(
  items: RowItems<D>,
): (RowItems<D>[number] & StackPositionMeta<D>)[] {
  const itemsGroupedByExactOverlap = new Map<string, RowItemWithDateKey<D>[]>()

  const itemsWithKeys = items
    .filter(notEmptyFields('startDate'))
    .map((item) => {
      const dateKey = normalizedDateKey(item)

      if (!itemsGroupedByExactOverlap.has(dateKey)) {
        itemsGroupedByExactOverlap.set(dateKey, [])
      }

      itemsGroupedByExactOverlap.get(dateKey)?.push({ ...item, dateKey })

      return { ...item, dateKey }
    })

  return itemsWithKeys.map((item) => {
    const overlapGroup = itemsGroupedByExactOverlap.get(item.dateKey) ?? []
    // Yes, there can be periods in overlapGroup when a period has no endDate and a moment has the same startDate.
    const momentsInGroup = overlapGroup.filter(
      (groupItem): groupItem is MomentItem<D> & { dateKey: string } =>
        !isPeriodItem(groupItem),
    )
    // Keep track of which moments overlap this one so that their tooltips can be merged.
    // Only populated when there's an actual overlap, i.e. more than just itself.
    const overlappingMoments =
      !isPeriodItem(item) && momentsInGroup.length > 1
        ? momentsInGroup
        : undefined

    if (isPeriodItem(item)) {
      const { moments, ...periodItem } = item

      return {
        ...periodItem,
        stackPosition: computeStackPositions({
          itemsInGroup: overlapGroup,
          id: item.id,
        }),
        // This will be undefined for periods, but we include it here for type consistency with moments.
        overlappingMoments,
        ...(moments?.length
          ? {
              moments: getItemsWithStackPosition(moments),
            }
          : {}),
      }
    }

    return {
      ...item,
      stackPosition: computeStackPositions({
        itemsInGroup: momentsInGroup,
        id: item.id,
      }),
      overlappingMoments,
    }
  })
}

export const normalizedDateKey = ({
  startDate,
  endDate,
}: {
  startDate: DateType
  endDate?: Optional<DateType>
}) =>
  endDate
    ? `${dayjs(startDate).startOf('month').valueOf()}-${dayjs(endDate).startOf('month').valueOf()}`
    : `${dayjs(startDate).startOf('month').valueOf()}`

// Highest being 1
const getHighestPriorityItem = <
  T extends { id: Optional<string>; priority?: number },
>(
  itemsInGroup: T[],
) =>
  itemsInGroup.reduce<Optional<T>>((acc, item) => {
    if (!acc?.priority || (item.priority && item.priority <= acc.priority)) {
      return item
    }

    return acc
  }, null)

const computeStackPositions = <D extends DateType>({
  itemsInGroup,
  id,
}: {
  itemsInGroup: RowItemWithDateKey<D>[]
  id: Optional<string>
}): StackPosition => {
  const item = itemsInGroup.find((item) => item.id === id)

  // Preserve domain-specific visual ordering when the item already provides it. Only done for money lane. See prepareMoneyMomentItems().
  if (item?.stackPosition) {
    return item.stackPosition
  }

  const highestPriorityId = getHighestPriorityItem(itemsInGroup)

  return highestPriorityId && highestPriorityId.id === id ? 'front' : 'behind'
}

export const timelineZIndexes = {
  period: cx('z-1'),
  momentBehind: cx('z-2'),
  momentFront: cx('z-3'),
  currentDateLine: cx('z-4'),
}
